import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { HelpIcon } from './HelpIcon'
import s from './MultiSelect.module.scss'

export interface MultiSelectOption {
  /** Was gespeichert und weitergegeben wird — eine Kennung, kein Anzeigetext. */
  value: string
  /** Was der Benutzer liest. */
  label: string
  /**
   * Eine Zeile Erläuterung unter dem Label. Für Fälle, in denen weder Kennung
   * noch Kürzel eindeutig sind — bei Börsen tragen drei verschiedene das
   * Kürzel BSE, und erst der volle Name sagt, welche gemeint ist.
   */
  hint?: string
}

interface MultiSelectProps {
  label?: string
  help?: ReactNode
  options: MultiSelectOption[]
  /** Die gewählten `value`. Die Komponente hält keinen eigenen Zustand. */
  selected: string[]
  onChange: (selected: string[]) => void
  /** Was im Feld steht, solange nichts gewählt ist. */
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
}

/**
 * Auswahl mehrerer Werte aus einer langen Liste, mit Suche.
 *
 * Gesucht wird in LABEL UND VALUE. Bei Handelsplätzen heißt das: „nasdaq"
 * findet ihn über das Kürzel, „XNAS" über den MIC. Wer das eine kennt, muss
 * nicht das andere nachschlagen — und genau das ist der Grund, warum ein
 * einfaches `<select multiple>` hier nicht reicht.
 *
 * Der Zustand liegt beim Aufrufer. Die Auswahl gehört meist in die Adresse
 * (teilbar, überlebt das Neuladen), und eine Komponente mit eigenem Zustand
 * müsste dann zwei Wahrheiten abgleichen.
 */
export function MultiSelect({
  label, help, options, selected, onChange,
  placeholder = 'alle', searchPlaceholder = 'suchen …', disabled,
}: MultiSelectProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [suche, setSuche] = useState('')
  //: Welcher Eintrag bei Enter geschaltet wird. Getrennt vom Fokus, denn der
  //: bleibt im Suchfeld — sonst müßte man zwischen Tippen und Auswählen
  //: hin- und herspringen.
  const [aktiv, setAktiv] = useState(0)

  const wurzel = useRef<HTMLDivElement>(null)
  const sucheRef = useRef<HTMLInputElement>(null)
  const listeRef = useRef<HTMLDivElement>(null)

  const treffer = useMemo(() => {
    const begriff = suche.trim().toLowerCase()
    if (!begriff) return options
    // Label ODER Value: Wer "XNAS" eingibt, meint den MIC; wer "nasdaq"
    // eingibt, das Kürzel. Beides führt zum selben Eintrag.
    const passend = options.filter((o) =>
      o.label.toLowerCase().includes(begriff) ||
      o.value.toLowerCase().includes(begriff) ||
      (o.hint?.toLowerCase().includes(begriff) ?? false))

    // Nach Passgenauigkeit ordnen, nicht nach der Reihenfolge der Liste.
    //
    // Ohne das führte "nasdaq" zu einer Falle: Es trifft fünf Börsen — Nasdaq,
    // Nasdaq Copenhagen, Helsinki, Island, Stockholm —, und die Liste steht
    // nach MIC sortiert. Oben stünde XCSE (Kopenhagen), und Enter wählte die
    // aus, obwohl man die NASDAQ meinte.
    const rang = (o: MultiSelectOption) => {
      const label = o.label.toLowerCase()
      const value = o.value.toLowerCase()
      if (label === begriff || value === begriff) return 0   // genau das
      if (label.startsWith(begriff)) return 1                // fängt so an
      if (value.startsWith(begriff)) return 2
      if (label.includes(begriff)) return 3                  // steht drin
      return 4                                               // nur im Namen
    }
    // Stabil: Bei gleichem Rang bleibt die Reihenfolge der Liste erhalten,
    // damit die Anzeige nicht bei jedem Tastendruck umspringt.
    return [...passend].sort((a, b) => rang(a) - rang(b))
  }, [options, suche])

  // Nach dem Tippen zeigt die Markierung auf den ersten Treffer. Bliebe sie
  // stehen, schaltete Enter einen Eintrag, den man gar nicht mehr sieht.
  useEffect(() => { setAktiv(0) }, [suche])

  // Beim Öffnen in das Suchfeld: Wer die Liste aufklappt, will meist tippen.
  useEffect(() => {
    if (open) sucheRef.current?.focus()
    else setSuche('')
  }, [open])

  // Klick außerhalb schließt. Ohne das bliebe die Liste offen, während man
  // längst woanders arbeitet, und verdeckte die Tabelle darunter.
  useEffect(() => {
    if (!open) return
    function aussen(event: MouseEvent) {
      if (!wurzel.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', aussen)
    return () => document.removeEventListener('mousedown', aussen)
  }, [open])

  // Die Markierung im sichtbaren Bereich halten — sonst wandert sie beim
  // Blättern mit den Pfeiltasten aus dem Fenster.
  useEffect(() => {
    if (!open) return
    listeRef.current?.querySelector<HTMLElement>('[data-aktiv="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [aktiv, open])

  function umschalten(value: string) {
    onChange(selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value])
  }

  function tasten(event: React.KeyboardEvent) {
    if (event.key === 'Escape') { setOpen(false); return }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!treffer.length) return
      const richtung = event.key === 'ArrowDown' ? 1 : -1
      setAktiv((i) => (i + richtung + treffer.length) % treffer.length)
      return
    }
    if (event.key === 'Enter' && treffer[aktiv]) {
      // Kein Absenden des umgebenden Formulars: Enter wählt hier aus.
      event.preventDefault()
      umschalten(treffer[aktiv].value)
    }
  }

  //: Was im geschlossenen Feld steht. Bei einem Treffer sein Name, sonst die
  //: Anzahl — fünf Kürzel nebeneinander sprengen die Feldbreite, und
  //: abgeschnitten wäre die Angabe irreführend.
  const gewaehlt = options.filter((o) => selected.includes(o.value))
  const zusammenfassung =
    gewaehlt.length === 0 ? placeholder
      : gewaehlt.length === 1 ? gewaehlt[0].label
        : `${gewaehlt.length} gewählt`

  return (
    <div className={s.wrap} ref={wurzel}>
      {label && (
        <span className={s.label} id={`${id}-label`}>
          {label}
          {help && <HelpIcon term={label}>{help}</HelpIcon>}
        </span>
      )}

      <button type="button" className={s.feld} disabled={disabled}
              aria-haspopup="listbox" aria-expanded={open}
              aria-labelledby={label ? `${id}-label` : undefined}
              onClick={() => setOpen((o) => !o)}
              onKeyDown={(event) => {
                if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
                  event.preventDefault()
                  setOpen(true)
                }
              }}>
        <span className={selected.length ? s.wert : s.leer}>
          {zusammenfassung}
        </span>
        <span className={s.pfeil} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className={s.klappe}>
          <input ref={sucheRef} className={s.suche} type="text"
                 placeholder={searchPlaceholder} value={suche}
                 onChange={(event) => setSuche(event.target.value)}
                 onKeyDown={tasten}
                 aria-label={label ? `${label} durchsuchen` : 'durchsuchen'} />

          <div className={s.liste} role="listbox" aria-multiselectable="true"
               ref={listeRef}>
            {treffer.length === 0 && (
              <p className={s.nichts}>Kein Eintrag passt zu „{suche}“.</p>
            )}
            {treffer.map((option, index) => {
              const an = selected.includes(option.value)
              return (
                <div key={option.value} role="option" aria-selected={an}
                     data-aktiv={index === aktiv}
                     className={index === aktiv ? s.zeileAktiv : s.zeile}
                     onMouseEnter={() => setAktiv(index)}
                     // mousedown statt click: Der Klick nähme dem Suchfeld
                     // erst den Fokus, und der Aussenklick-Wächter schlösse
                     // die Liste, bevor die Auswahl ankommt.
                     onMouseDown={(event) => {
                       event.preventDefault()
                       umschalten(option.value)
                     }}>
                  <span className={s.kasten} aria-hidden="true">
                    {an ? '✓' : ''}
                  </span>
                  <span className={s.text}>
                    <span className={s.optionLabel}>{option.label}</span>
                    {option.hint && <span className={s.hint}>{option.hint}</span>}
                  </span>
                  <span className={s.value}>{option.value}</span>
                </div>
              )
            })}
          </div>

          <div className={s.fuss}>
            <span className={s.zaehler}>
              {selected.length
                ? `${selected.length} von ${options.length} gewählt`
                : `${options.length} Einträge`}
            </span>
            <button type="button" className={s.leeren}
                    disabled={!selected.length}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      onChange([])
                    }}>
              Auswahl leeren
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
